import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { ROLE_LABELS, hasRole, useApp } from "../context/AppContext";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import logoIcon from "../assets/logo_icon.png";

// Inline SVG icons keep us free of extra deps.
const Icon = {
  Dashboard: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <rect x="3" y="3" width="7" height="9" rx="1.5" />
      <rect x="14" y="3" width="7" height="5" rx="1.5" />
      <rect x="14" y="12" width="7" height="9" rx="1.5" />
      <rect x="3" y="16" width="7" height="5" rx="1.5" />
    </svg>
  ),
  Box: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <path d="m3.3 7 8.7 5 8.7-5" />
      <path d="M12 22V12" />
    </svg>
  ),
  Brain: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M12 5a3 3 0 0 0-3-3 3 3 0 0 0-3 3 3 3 0 0 0-2 5 3 3 0 0 0 0 4 3 3 0 0 0 2 5 3 3 0 0 0 3 3 3 3 0 0 0 3-3z" />
      <path d="M12 5a3 3 0 0 1 3-3 3 3 0 0 1 3 3 3 3 0 0 1 2 5 3 3 0 0 1 0 4 3 3 0 0 1-2 5 3 3 0 0 1-3 3 3 3 0 0 1-3-3z" />
    </svg>
  ),
  Chart: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M3 3v18h18" />
      <path d="M7 14l4-4 4 4 5-6" />
    </svg>
  ),
  Rocket: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M14 4s4 0 6 2 2 6 2 6-3 .5-5 .5S12 11 12 11" />
      <path d="M9 11s-2-2-2-4 1-4 1-4 4 1 6 3l1 2-3 6-3-3z" />
      <path d="m9 15-3 3 3 3 3-3" />
      <circle cx="15" cy="9" r="1" />
    </svg>
  ),
  Cpu: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <rect x="5" y="5" width="14" height="14" rx="2" />
      <rect x="9" y="9" width="6" height="6" />
      <path d="M9 1v3M15 1v3M9 20v3M15 20v3M1 9h3M1 15h3M20 9h3M20 15h3" />
    </svg>
  ),
  History: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
      <path d="M3 3v5h5" />
      <path d="M12 7v5l3 2" />
    </svg>
  ),
  Settings: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
    </svg>
  ),
  ChevronsLeft: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="m11 17-5-5 5-5M18 17l-5-5 5-5" />
    </svg>
  ),
  User: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  Logout: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="m16 17 5-5-5-5" />
      <path d="M21 12H9" />
    </svg>
  ),
  Play: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <polygon points="6 3 20 12 6 21 6 3" />
    </svg>
  ),
  Book: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  ),
  Code: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="m16 18 6-6-6-6M8 6l-6 6 6 6" />
    </svg>
  ),
};

// docs/tasks/35: the fleet first (cabinet type -> cabinet -> opening).
const NAV = [
  { to: "/", label: "Fleet", icon: Icon.Dashboard, roles: ["tenant_admin", "product_manager", "ml_operator", "device_operator", "auditor"] },
  { to: "/products", label: "Products", icon: Icon.Box, roles: ["tenant_admin", "product_manager", "auditor"] },
  { to: "/test", label: "Test console", icon: Icon.Play, roles: ["tenant_admin", "device_operator"] },
  { to: "/classifier", label: "Classifier", icon: Icon.Box, roles: ["tenant_admin", "ml_operator", "device_operator", "auditor"] },
  { to: "/purchases", label: "Purchases", icon: Icon.History, roles: ["tenant_admin", "auditor"] },
  { to: "/integrations", label: "Integrations", icon: Icon.Code, roles: ["tenant_admin", "auditor"] },
  { to: "/users", label: "Users", icon: Icon.Settings, roles: ["tenant_admin"] },
  { to: "/docs", label: "Documentation", icon: Icon.Book, roles: ["tenant_admin", "product_manager", "ml_operator", "device_operator", "auditor"] },
  { to: "/api-docs", label: "API reference", icon: Icon.Code, roles: ["tenant_admin", "product_manager", "ml_operator", "device_operator", "auditor"] },
];

const COLLAPSED_KEY = "ainaopen.sidebar_collapsed";

function initials(user) {
  const src = (user?.display_name || user?.email || user?.user_id || "?").trim();
  const parts = src.split(/\s+/).filter(Boolean);
  const letters = parts.length >= 2 ? parts[0][0] + parts[1][0] : src.slice(0, 2);
  return letters.toUpperCase();
}

function UserMenu({ user, onSignOut }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-2 py-1 hover:bg-slate-50"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="grid h-7 w-7 place-items-center rounded-full bg-brand-500 text-xs font-semibold text-white">
          {initials(user)}
        </span>
        <span className="hidden text-sm font-medium text-slate-800 sm:inline">{user.display_name || user.user_id}</span>
        <svg className="h-4 w-4 text-slate-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-2 w-64 rounded-lg border border-slate-200 bg-white shadow-lg"
        >
          <div className="border-b border-slate-100 px-4 py-3">
            <div className="text-sm font-semibold text-slate-900">{user.display_name || user.user_id}</div>
            <div className="truncate text-xs text-slate-500">{user.email || user.user_id}</div>
            <div className="mt-1 text-xs text-slate-500">
              {user.roles.map((r) => ROLE_LABELS[r] ?? r).join(", ")}
            </div>
          </div>
          <div className="py-1 text-sm">
            <Link to="/settings" onClick={() => setOpen(false)} className="flex items-center gap-2 px-4 py-2 text-slate-700 hover:bg-slate-50" role="menuitem">
              <Icon.User className="h-4 w-4" /> Profile
            </Link>
            <Link to="/settings" onClick={() => setOpen(false)} className="flex items-center gap-2 px-4 py-2 text-slate-700 hover:bg-slate-50" role="menuitem">
              <Icon.Settings className="h-4 w-4" /> Account settings
            </Link>
          </div>
          <div className="border-t border-slate-100 py-1 text-sm">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onSignOut();
              }}
              className="flex w-full items-center gap-2 px-4 py-2 text-left text-red-600 hover:bg-red-50"
              role="menuitem"
            >
              <Icon.Logout className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SideNavItem({ item, collapsed }) {
  const { icon: IconCmp, to, label } = item;
  return (
    <NavLink
      to={to}
      end={to === "/"}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        clsx(
          "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900",
          collapsed && "justify-center px-2",
          isActive && "bg-brand-50 text-brand-700 hover:bg-brand-50"
        )
      }
    >
      <IconCmp className="h-5 w-5 shrink-0" />
      {!collapsed && <span className="truncate">{label}</span>}
    </NavLink>
  );
}

export function Layout() {
  const { user, signOut } = useApp();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, [collapsed]);

  const onSignOut = () => {
    signOut();
    navigate("/login", { replace: true });
  };

  return (
    <div className="flex min-h-screen">
      <aside
        className={clsx(
          "hidden shrink-0 flex-col border-r border-slate-200 bg-white transition-[width] duration-150 md:flex",
          collapsed ? "w-16" : "w-64"
        )}
      >
        <div
          className={clsx(
            "flex h-14 items-center border-b border-slate-100 px-3",
            collapsed ? "flex-col justify-center gap-2 py-2 h-auto" : "justify-between"
          )}
        >
          <Link to="/" className="flex items-center gap-2 min-w-0">
            <img src={logoIcon} alt="ainaopen" className="h-8 w-8 shrink-0 rounded-md object-contain" />
            {!collapsed && <span className="truncate text-base font-semibold">ainaopen vision</span>}
          </Link>
          <button
            type="button"
            className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
            onClick={() => setCollapsed((v) => !v)}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <Icon.ChevronsLeft className={clsx("h-4 w-4 transition-transform", collapsed && "rotate-180")} />
          </button>
        </div>

        <nav className="flex-1 space-y-1 p-2">
          {NAV.filter((item) => item.section || hasRole(user, ...item.roles)).map((item) => (
            item.section
              ? (!collapsed && <div key={item.section} className="px-3 pb-1 pt-4 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{item.section}</div>)
              : <SideNavItem key={item.to} item={item} collapsed={collapsed} />
          ))}
        </nav>

        <div className="border-t border-slate-100 p-2">
          <SideNavItem item={{ to: "/settings", label: "Settings", icon: Icon.Settings }} collapsed={collapsed} />
        </div>
      </aside>

      <div className="flex min-h-screen flex-1 flex-col">
        <header className="flex h-14 items-center justify-between border-b border-slate-200 bg-white px-6">
          <div className="md:hidden font-semibold">ainaopen vision</div>
          <div className="ml-auto flex items-center gap-3 text-sm">
            {user ? (
              <UserMenu user={user} onSignOut={onSignOut} />
            ) : (
              <Link className="btn-primary" to="/login">
                Sign in
              </Link>
            )}
          </div>
        </header>
        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
